"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

import { z } from "zod";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

import { api } from "~/utils/api";
import { toastHandler } from "~/components/ToastHandler";
import Spinner from "~/components/themed/spinner";

import { communityStepsTitles } from "./_utils/communityUtils";
import { CreateCommunityStep1 } from "./CreateCommunitySteps/CreateCommunityStep1";

import { Form } from "../shadcn/Form";
import { Button } from "../themed/Button";
import LoginModal from "../themed/LoginModal";
import Modal from "../themed/Modal";
import { communityFormStepOne } from "./_types/CommunityTypes";

const formSchemas = [communityFormStepOne];

type FormData = z.infer<typeof communityFormStepOne>;
type Props = {
    isVisible: boolean;
    setVisible: (show: boolean, routing?: boolean) => void; //routing field is used in the homepage
};

export const CommunityCreateModal = ({ isVisible, setVisible }: Props) => {
    const router = useRouter();
    const session = useSession();

    const createCommunity = api.community.createCommunity.useMutation();
    const updateCommunity = api.community.updateCommunity.useMutation();

    const [step, setStep] = useState<number>(0);
    const [skipSteps, setSkipSteps] = useState<number[]>([]); //allows us to skip some steps
    const [loading, setLoading] = useState(false);

    const currentSchema = formSchemas[step];

    const form = useForm<FormData>({
        resolver: currentSchema ? zodResolver(currentSchema) : undefined,
        defaultValues: {
            communityName: "",
            communitySlug: "",
            communityEventDescriptions: "",
            socialLinks: [],
        },
        mode: "onChange",
        criteriaMode: "all",
    });

    const formValues = form.watch();

    const isShareLinkStepRequired = false;

    // if communitySize is greater than 100K we add an extra step to show the meeting link modal
    const isLastStep = step === formSchemas.length - 1;

    const normalizeUrl = (url: string) =>
        url.startsWith("http") ? url : `https://${url}`;

    const handleCommunityCreation = async (data: FormData) => {
        const hostGoalsConfig = {
            isAutoPromoteToPreapprovedHostsOn: true,
            numberOfApprovalsTillHostPromotion: "1",
        };

        const communityData = {
            communityName: data?.communityName,
            communitySlug: data?.communitySlug,
            communityWebsite: data?.communityWebsite
                ? normalizeUrl(data.communityWebsite)
                : undefined,
            communityEventDescriptions: data?.communityEventDescriptions ?? "",
            socials: data.socialLinks,
            isHostAllowedToSeeEmailsOfHostedEvents: false,
            ...hostGoalsConfig,
        };

        try {
            setLoading(true);
            const community = await toastHandler({
                loadingMessage: "Creating community...",
                successMessage: "Community created successfully!",
                errorMessage: "Failed to create community. Please try again.",
                operation: () => createCommunity.mutateAsync(communityData),
            });

            if (!community) return;

            // step 2: update the image path (we use a random bucket while the user is filling in the form since community doesn't exist yet)
            if (data.avatarImage) {
                await toastHandler({
                    loadingMessage: "Updating community image...",
                    successMessage: "Community image updated successfully!",
                    errorMessage:
                        "Failed to update community image. Please try again.",
                    operation: () =>
                        updateCommunity.mutateAsync({
                            slug: community.slug,
                            image: data.avatarImage ?? "",
                            updateImage: {
                                image: data.avatarImage ?? "",
                                from: `community`,
                                to: `community/${community.id}`,
                            },
                        }),
                });
            }

            router.push(`/beta/${community.slug}`);
        } catch (error) {
            setLoading(false);
            toast.error(
                "Error Finalising processing community. Please try again.",
            );
            console.error("Community creation error:", error);
        }
    };

    const isCurrentStepValid = () => {
        if (!currentSchema) return true;

        const result = currentSchema.safeParse(formValues);
        return result.success;
    };

    /** gets the next step */
    const getNextStep = (
        currentStep: number,
        direction: "forward" | "backward",
    ) => {
        let nextStep = currentStep;

        do {
            nextStep = direction === "forward" ? nextStep + 1 : nextStep - 1;
            if (nextStep < 0 || nextStep >= formSchemas.length) return 0;
        } while (skipSteps.includes(nextStep));

        return nextStep;
    };

    const resetAndCloseForm = () => {
        form.reset();
        setVisible(false, true);
        setSkipSteps([]);
        setStep(0);
    };

    const onSubmit = async () => {
        if (isLastStep) {
            await handleCommunityCreation(formValues);
            resetAndCloseForm();
            return;
        } else {
            const nextStep = getNextStep(step, "forward");
            setStep(nextStep);
        }
    };

    const handleBack = () => {
        if (step === 0) {
            resetAndCloseForm();
            return;
        }

        const prevStep = getNextStep(step, "backward");
        setStep(prevStep);
    };

    if (session.status === "unauthenticated") {
        return (
            <LoginModal
                isOpen={isVisible}
                setIsOpen={setVisible}
                title="Sign in to create a community"
                description="Create your community page and start hosting events."
                callbackUrl="/beta/community/create"
            />
        );
    }

    if (loading) {
        return <Spinner />;
    }

    return (
        <Modal
            open={isVisible}
            onOpenChange={() => {
                resetAndCloseForm();
            }}
            title={communityStepsTitles[step]?.title}
            description={communityStepsTitles[step]?.description}
            className="flex max-h-[90vh] flex-col"
        >
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="modal-content">
                        {step === 0 && <CreateCommunityStep1 />}
                    </div>

                    <div className="mt-6 flex justify-between">
                        <Button
                            type="button"
                            variant="tertiary"
                            onClick={handleBack}
                            className="h-10"
                        >
                            {step === 0 ? (
                                "Cancel"
                            ) : (
                                <span className="flex items-center gap-2">
                                    <FaArrowLeft size={12} />
                                    <span>Go back</span>
                                </span>
                            )}
                        </Button>

                        <Button
                            type="submit"
                            className="h-10"
                            disabled={!isCurrentStepValid()}
                        >
                            {isLastStep ? (
                                "Create"
                            ) : (
                                <span className="flex items-center gap-2">
                                    <span>Next step</span>
                                    <FaArrowRight size={12} />
                                </span>
                            )}
                        </Button>
                    </div>
                </form>
            </Form>
        </Modal>
    );
};
